import './PostalCodesInput.scss';
import React, { useEffect, useRef, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Input, Space, Tag, Tooltip, Select, notification } from 'antd';
import copy from 'copy-to-clipboard';

const PostalCodeInput = ({ postalCodes, setPostalCodes, selectedCountry, setSelectedCountry }) => {
  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [editInputIndex, setEditInputIndex] = useState(-1);
  const [editInputValue, setEditInputValue] = useState('');
  const { getCode } = require('country-list');
  const inputRef = useRef(null);
  const editInputRef = useRef(null);

  useEffect(() => {
    if (inputVisible) {
      inputRef.current?.focus();
    }
  }, [inputVisible]);

  useEffect(() => {
    editInputRef.current?.focus();
  }, [editInputValue]);

  const handleClose = (removedpostalCode) => {
    const newpostalCodes = postalCodes.filter((postalCode) => postalCode !== removedpostalCode);
    setPostalCodes(newpostalCodes);
  };

  const showInput = () => {
    setInputVisible(true);
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputConfirm = () => {
    if (inputValue && !postalCodes.includes(inputValue)) {
      const match = inputValue.match(/[0-9]{4}(?:\s?[A-Za-z]{2})?/g); // Match PC4, PC6, and PC6 with space
      if (match && match.length) {
        const validPostalCodes = match.flatMap((code) => {
          const trimmedCode = code.replace(/\s+/g, '').toUpperCase(); // Remove spaces and ensure uppercase
          if (!postalCodes.includes(trimmedCode)) {
            return [trimmedCode];
          }
          return [];
        });

        if (validPostalCodes.length > 0) {
          setPostalCodes([...postalCodes, ...validPostalCodes]);
        } else {
          alert('Invalid input or duplicate!');
        }
      } else {
        alert('Invalid input!');
      }
    }

    setInputVisible(false);
    setInputValue('');
  };

  const handleEditInputChange = (e) => {
    setEditInputValue(e.target.value);
  };

  const handleEditInputConfirm = () => {
    const newPostalCodes = [...postalCodes];
    newPostalCodes[editInputIndex] = editInputValue;
    setPostalCodes(newPostalCodes);
    setEditInputIndex(-1);
    setEditInputValue('');
  };

  const tagInputStyle = {
    width: 64,
    height: 22,
    marginInlineEnd: 8,
    verticalAlign: 'top',
  };

  const tagPlusStyle = {
    height: 22,
    borderStyle: 'dashed',
  };

  const onCountrySelect = (country) => {
    setSelectedCountry(country);
    setPostalCodes([]);
  };

  const onClickCopy = (value) => {
    copy(value);
    notification.success({ message: 'Copied' });
  };

  return (
    <>
      <h5>Select a Country</h5>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Select
          size="large"
          className="CountrySelect"
          placeholder="Select a Country..."
          onChange={(country) => onCountrySelect(country)}
          defaultValue={selectedCountry}
        >
          <Select.Option value={getCode('Netherlands')}>Netherlands</Select.Option>
          <Select.Option value={getCode('Belgium')}>Belgium</Select.Option>
        </Select>
        <div onClick={() => onClickCopy(postalCodes)} style={{ marginLeft: 'auto' }}>
          <img src="/icons/file_copy.svg" alt="Copy Postal Codes" />
        </div>
      </div>
      <br />
      {selectedCountry !== null && (
        <Space size={[0, 8]} wrap>
          {postalCodes.map((postalCode, index) => {
            if (editInputIndex === index) {
              return (
                <Input
                  ref={editInputRef}
                  key={postalCode}
                  size="large"
                  style={tagInputStyle}
                  value={editInputValue}
                  onChange={handleEditInputChange}
                  onBlur={handleEditInputConfirm}
                  onPressEnter={handleEditInputConfirm}
                />
              );
            }
            const isLongTag = postalCode.length > 20;
            const tagElem = (
              <Tag
                key={postalCode}
                closable={true}
                style={{ userSelect: 'none' }}
                onClose={() => handleClose(postalCode)}
              >
                <span
                  onDoubleClick={(e) => {
                    if (index !== 0) {
                      setEditInputIndex(index);
                      setEditInputValue(postalCode);
                      e.preventDefault();
                    }
                  }}
                >
                  {isLongTag ? `${postalCode.slice(0, 20)}...` : postalCode}
                </span>
              </Tag>
            );
            return isLongTag ? (
              <Tooltip title={postalCode} key={postalCode}>
                {tagElem}
              </Tooltip>
            ) : (
              tagElem
            );
          })}
          {inputVisible ? (
            <Input
              ref={inputRef}
              type="text"
              size="large"
              style={tagInputStyle}
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputConfirm}
              onPressEnter={handleInputConfirm}
            />
          ) : (
            <Tag style={tagPlusStyle} icon={<PlusOutlined />} onClick={showInput}>
              Add Postal Code
            </Tag>
          )}
        </Space>
      )}
    </>
  );
};

export default PostalCodeInput;
